import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Collapse, IconButton, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { WalletMeter } from '../../../components/_mui';
import {
  BACK_ARIA_LABEL,
  SEARCH_FIELD_MAX_WIDTH,
  SEARCH_FIELD_MIN_WIDTH,
  STICKY_TOOLBAR_CLIP_PATH,
  STICKY_TOOLBAR_CONDENSE_TRAVEL,
  STICKY_TOOLBAR_ELEVATION,
  STICKY_TOOLBAR_TOP_DESKTOP,
  STICKY_TOOLBAR_TOP_MOBILE,
  STICKY_TOOLBAR_Z_INDEX,
  TOOLBAR_BACK_SLOT_WIDTH,
  TOOLBAR_PADDING_Y,
  TOOLBAR_ROW_GAP,
} from '../../../constants/marketplace';
import { useStickyToolbar } from '../../../hooks/useStickyToolbar';
import { selectContentBackgroundColor } from '../../../store/selectors/derived';
import { selectCartCount, selectCreditsRemaining } from '../../../store/selectors/marketplace';
import { invertShadowY } from '../../../util/ui';
import { FitsBudgetToggle } from './FitsBudgetToggle';
import { MarketplaceFilterMenus } from './MarketplaceFilterMenu';
import { MarketplaceFilters } from './MarketplaceFilters';
import { MarketplaceSearchField } from './MarketplaceSearchField';
import { SupplierFilterChip } from './SupplierFilterChip';

interface IProps {
  onOpenCart: () => void;
  isBackShown: boolean;
  onBack: () => void;
}

type TContentBgColor = ReturnType<typeof selectContentBackgroundColor>;

const getStickyBarSx = (stickyTop: number, isStuck: boolean, contentBgColor: TContentBgColor): SxProps<Theme> => ({
  position: 'sticky',
  top: `${stickyTop}px`,
  zIndex: STICKY_TOOLBAR_Z_INDEX,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: ({ palette }) => contentBgColor || palette.grey[100],
  width: '100vw',
  marginInline: 'calc(50% - 50vw)',
  paddingInline: 'calc(50vw - 50%)',
  clipPath: STICKY_TOOLBAR_CLIP_PATH,
  transition: ({ transitions }) => transitions.create(['box-shadow'], { duration: transitions.duration.shorter }),
  ...(isStuck && { boxShadow: ({ shadows }) => invertShadowY(shadows[STICKY_TOOLBAR_ELEVATION]) }),
});

const backButtonSx: SxProps<Theme> = { color: 'action.active', flexShrink: 0 };

export const MarketplaceToolbar: React.FC<IProps> = (props) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));

  return isMobileViewport ? <MobileToolbar {...props} /> : <DesktopToolbar {...props} />;
};

const desktopSentinelSx: SxProps<Theme> = { height: 1 };

const desktopSearchFieldSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flex: `1 1 ${SEARCH_FIELD_MIN_WIDTH}`,
  minWidth: SEARCH_FIELD_MIN_WIDTH,
  maxWidth: SEARCH_FIELD_MAX_WIDTH,
};

// The search min-width is a percentage, so it is ignored when sizing this group; wrap the menus
// under the search instead of letting them overflow into the controls.
const desktopLeadSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1.5,
  flex: '1 1 auto',
  minWidth: 0,
};

const desktopControlsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexShrink: 0,
};

// Wraps rather than shrinks: the content-sized toggle and wallet drop to their own row when they
// no longer fit beside the search field, and the chips follow as the third row.
const desktopTopRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 1.5,
};

const DesktopToolbar: React.FC<IProps> = ({ onOpenCart, isBackShown, onBack }) => {
  const remaining = useSelector(selectCreditsRemaining);
  const cartCount = useSelector(selectCartCount);
  const contentBgColor = useSelector(selectContentBackgroundColor);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { isStuck } = useStickyToolbar({ sentinelRef, stickyTop: STICKY_TOOLBAR_TOP_DESKTOP });

  const barSx: SxProps<Theme> = useMemo(
    () => ({
      ...getStickyBarSx(STICKY_TOOLBAR_TOP_DESKTOP, isStuck, contentBgColor),
      gap: 1.5,
      mt: 2,
      mb: 0,
      py: 1.25,
    }),
    [isStuck, contentBgColor],
  );

  return (
    <>
      <Box ref={sentinelRef} sx={desktopSentinelSx} aria-hidden />

      <Box sx={barSx}>
        <Box sx={desktopTopRowSx}>
          <Box sx={desktopLeadSx}>
            <Box sx={desktopSearchFieldSx}>
              {isStuck && isBackShown && (
                <IconButton onClick={onBack} aria-label={BACK_ARIA_LABEL} sx={backButtonSx}>
                  <ArrowBackIcon sx={{ fontSize: 24 }} />
                </IconButton>
              )}
              <MarketplaceSearchField />
            </Box>
            <MarketplaceFilterMenus />
            <SupplierFilterChip isDividerLeading />
          </Box>
          <Box sx={desktopControlsSx}>
            <FitsBudgetToggle />
            <WalletMeter remaining={remaining} onClick={onOpenCart} pickCount={cartCount} />
          </Box>
        </Box>
        <MarketplaceFilters withMenus={false} />
      </Box>
    </>
  );
};

const mobileSentinelSx: SxProps<Theme> = { height: 0, mt: 2 };

const mobileSearchRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  pb: `${TOOLBAR_ROW_GAP}px`,
};

const backSlotSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  transition: ({ transitions }) =>
    transitions.create(['width', 'opacity'], { duration: transitions.duration.standard }),
};

const mobileBackButtonSx: SxProps<Theme> = { ...backButtonSx, mr: 1 };

const mobileSearchFieldSx: SxProps<Theme> = { flex: 1, minWidth: 0 };

const mobileControlsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: `${TOOLBAR_ROW_GAP}px`,
};

const walletFillSx: SxProps<Theme> = { flex: 1 };

const mobileCategoriesSx: SxProps<Theme> = { pt: `${TOOLBAR_ROW_GAP}px` };

const MobileToolbar: React.FC<IProps> = ({ onOpenCart, isBackShown, onBack }) => {
  const remaining = useSelector(selectCreditsRemaining);
  const cartCount = useSelector(selectCartCount);
  const contentBgColor = useSelector(selectContentBackgroundColor);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [spacerHeight, setSpacerHeight] = useState(0);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const searchRowRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);

  const { isStuck, isCondensed } = useStickyToolbar({
    sentinelRef,
    stickyTop: STICKY_TOOLBAR_TOP_MOBILE,
    condenseAfter: STICKY_TOOLBAR_CONDENSE_TRAVEL,
    isSearchFocused,
  });

  useEffect(() => {
    if (!isCondensed) {
      setSpacerHeight(0);

      return;
    }

    setSpacerHeight((searchRowRef.current?.offsetHeight ?? 0) + (categoriesRef.current?.offsetHeight ?? 0));
  }, [isCondensed]);

  const isBackVisible = isStuck && isBackShown;

  const barSx: SxProps<Theme> = useMemo(
    () => ({ ...getStickyBarSx(STICKY_TOOLBAR_TOP_MOBILE, isStuck, contentBgColor), py: `${TOOLBAR_PADDING_Y}px` }),
    [isStuck, contentBgColor],
  );

  const animatedBackSlotSx: SxProps<Theme> = useMemo(
    () => ({ ...backSlotSx, width: isBackVisible ? TOOLBAR_BACK_SLOT_WIDTH : 0, opacity: isBackVisible ? 1 : 0 }),
    [isBackVisible],
  );

  const spacerSx: SxProps<Theme> = useMemo(
    () => ({
      height: `${spacerHeight}px`,
      transition: ({ transitions }) => transitions.create('height', { duration: transitions.duration.standard }),
    }),
    [spacerHeight],
  );

  return (
    <>
      <Box ref={sentinelRef} sx={mobileSentinelSx} aria-hidden />

      <Box sx={barSx}>
        <Collapse in={!isCondensed}>
          <Box ref={searchRowRef} sx={mobileSearchRowSx}>
            <Box sx={animatedBackSlotSx}>
              <IconButton
                onClick={onBack}
                aria-label={BACK_ARIA_LABEL}
                aria-hidden={!isBackVisible}
                tabIndex={isBackVisible ? 0 : -1}
                sx={mobileBackButtonSx}
              >
                <ArrowBackIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
            <Box sx={mobileSearchFieldSx}>
              <MarketplaceSearchField onFocusChange={setIsSearchFocused} />
            </Box>
          </Box>
        </Collapse>

        <Box sx={mobileControlsSx}>
          <FitsBudgetToggle />
          <WalletMeter remaining={remaining} onClick={onOpenCart} pickCount={cartCount} sx={walletFillSx} />
        </Box>

        <Collapse in={!isCondensed}>
          <Box ref={categoriesRef} sx={mobileCategoriesSx}>
            <MarketplaceFilters />
          </Box>
        </Collapse>
      </Box>

      <Box sx={spacerSx} aria-hidden />
    </>
  );
};
